import type { IProductAllocation } from '@/types/product';
import { request } from './client';
import type { RequestOptions } from './client';
import type {
  ImageObject,
  Order,
  OrderMedia,
  ResponseOrderImage,
  CrawledImage,
  CrawledImageStatusValue,
} from '@/types/types';

export function fetchOrder(
  orderId: number | string,
  params: Record<string, string | number>
): Promise<{ data: Order }> {
  return request('GET', `/v1/account/order/${orderId}`, params);
}

export function extractDataFromURL(url: string, type: any = null, requestOptions: RequestOptions = {}) {
  let payload: { url: string; type?: any } = { url };

  if (type) {
    payload = { ...payload, type };
  }
  return request('POST', '/v1/account/order/extract_from_url', {}, payload, requestOptions);
}

export function extractImagesFromURL(
  url: string,
  requestOptions: RequestOptions = {}
): Promise<{ bot_program_id: string | null; bot_id: number | null }> {
  return request('POST', '/v1/account/order/extract_images_from_url', {}, { url }, requestOptions);
}

export async function fetchAutocompleteFields(fields: string, params: object = {}): Promise<string[]> {
  const response = await request('GET', `/v1/account/orders/autocomplete/${fields}`, params);
  return response.data;
}

export async function getOrderImages(orderId: number | string): Promise<OrderMedia> {
  const response: { data: ResponseOrderImage[] } = await request('GET', `/v1/account/order/${orderId}/images`);
  const media: OrderMedia = { companyLogo: [], images: [] };
  if (Array.isArray(response.data)) {
    response.data.forEach((img) => {
      if (img.type === 'company_logo') media.companyLogo.push(img.image);
      else media.images.push(img.image);
    });
  }

  return media;
}

export async function changeOrderImages(
  orderId: number,
  media: OrderMedia,
  options: { filterSelected?: boolean; setDefaultCompanyLogo?: boolean } = {}
): Promise<ResponseOrderImage> {
  const { companyLogo, images } = media;
  const body: unknown[] = [];
  companyLogo.forEach((obj: ImageObject) =>
    body.push({
      hash: obj.hash,
      type: 'company_logo',
      sequence: 0,
      set_default_company_logo: !!options.setDefaultCompanyLogo,
    })
  );
  images.forEach((obj: ImageObject) => {
    if (options.filterSelected && !obj.selected) return;
    if (obj.type === 'crawled') {
      // crawled images already have a real pjas image_id (list endpoint creates the row).
      // the backend promotes the status=NEW placeholder to s3 during save.
      body.push({
        image_id: obj.id,
        type: 'crawled',
        sequence: 0,
      });
    } else {
      body.push({
        hash: obj.hash,
        type: 'image',
        sequence: 0,
      });
    }
  });
  const response = await request('POST', `/v1/account/order/${orderId}/images`, {}, body);
  return response.data;
}

export async function finishOrderCampaigns(orderId: string | number): Promise<Order> {
  const response = await request('POST', `/v1/account/order/${orderId}/finish`, {});
  return response.data;
}

export async function fetchAvailablePreviews(orderId: string | number): Promise<Record<string, any>> {
  const response = await request('GET', `/v1/account/order/${orderId}/previews/available`, {});
  return response.data;
}

export async function savePreviewOptions(
  orderId: string | number,
  previews: Record<string, string[]>
): Promise<Record<string, string>> {
  const response = await request('POST', `/v1/account/order/${orderId}/previews`, {}, { previews });
  return response.data;
}

export async function fetchOrderProducts(
  orderId: string | number,
  props?: Record<string, string | number>
): Promise<{ data: IProductAllocation[] }> {
  return request('GET', `/v1/account/order/${orderId}/products`, props || {});
}

export async function fetchOrderGenerateCampaignStatus(orderId: string | number) {
  return request('GET', `/v1/account/order/${orderId}/generate_campaign_status`);
}

export interface StatsQueryParams {
  provider?: string;
  merge?: 0 | 1;
  types?: string;
  date_format?: 'default' | 'atom' | 'unix';
  breakdown?: string;
  include_breakdown_info?: 0 | 1;
  transform?: 'default' | 'assoc' | 'series' | 'table' | 'timeseries';
}

export interface StatsAgg {
  index: number;
  sum: number;
  avg: number;
}

export interface StatsProviderEntry {
  provider: string;
  aggs: Record<string, StatsAgg>;
  data: any[];
}

export interface StatsResponse {
  aggs: Record<string, StatsAgg>;
  data: StatsProviderEntry[] | any[];
}

export function fetchOrderStats(orderId: string | number, params: StatsQueryParams = {}): Promise<StatsResponse> {
  return request('GET', `/v1/account/order/${orderId}/stats`, params as Record<string, any>);
}

export function fetchCampaignStats(
  orderId: string | number,
  campaignId: string | number,
  params: StatsQueryParams = {}
): Promise<StatsResponse> {
  return request('GET', `/v1/account/order/${orderId}/campaign/${campaignId}/stats`, params as Record<string, any>);
}

export function fetchOrderComparableStats(
  orderId: string | number,
  params: StatsQueryParams = {}
): Promise<StatsResponse> {
  return request('GET', `/v1/account/order/${orderId}/comparable_stats`, params as Record<string, any>);
}

export function fetchCampaignComparableStats(
  orderId: string | number,
  campaignId: string | number,
  params: StatsQueryParams = {}
): Promise<StatsResponse> {
  return request(
    'GET',
    `/v1/account/order/${orderId}/campaign/${campaignId}/comparable_stats`,
    params as Record<string, any>
  );
}

export interface StatsBreakdown {
  dimension: string;
  values: string[];
}

export interface StatsMetadata {
  providers: string[];
  metrics: string[];
  breakdowns: (string | StatsBreakdown)[];
  provider: string | null;
}

export function fetchStatsMetadata(
  orderId: string | number,
  params: { provider?: string } = {}
): Promise<StatsMetadata> {
  return request('GET', `/v1/account/order/${orderId}/stats/metadata`, params as Record<string, any>);
}

export function fetchCrawledImages(orderId: number | string) {
  return request('GET', `/v1/account/order/${orderId}/crawled-images`) as Promise<{
    data: { status: CrawledImageStatusValue; images: CrawledImage[] };
  }>;
}

export function crawledImageProxyUrl(orderId: number | string, resourceId: number): string {
  return `/v1/account/order/${orderId}/crawled-images/${resourceId}/proxy`;
}
